import React, { useState, useRef, useEffect, useCallback } from 'react';
import * as LucideIcons from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const ServiceCard: React.FC<{ service: any; index: number; isMobile?: boolean; totalServices: number; inquireLabel: string }> = ({ service, index, isMobile, totalServices, inquireLabel }) => {
  const [mousePos, setMousePos] = useState<{ x: number; y: number } | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const IconComponent = (LucideIcons as any)[service.iconName] || LucideIcons.Code2;
  const serviceNumber = (index + 1).toString().padStart(2, '0');

  return (
    <div 
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setMousePos(null);
      }}
      className={`group relative flex flex-col ${isMobile ? 'p-6 sm:p-8' : 'p-8 sm:p-10'} rounded-[2rem] bg-white border border-slate-200 transition-all duration-300 hover:border-indigo-400 hover:shadow-2xl overflow-hidden shadow-sm h-full`}
    >
      {/* Rotating Glowing Dot Orbit around Card Border on Hover */}
      <div className={`absolute inset-0 pointer-events-none rounded-[2rem] overflow-hidden transition-opacity duration-300 z-20 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
        <svg className="w-full h-full overflow-visible">
          <defs>
            <filter id={`orbit-glow-${index}-${isMobile ? 'm' : 'd'}`} x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="3" result="blur" />
              <feMerge>
                <feMergeNode in="blur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <linearGradient id={`dotGradient-${index}-${isMobile ? 'm' : 'd'}`} x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#a5b4fc" stopOpacity="0" />
              <stop offset="60%" stopColor="#6366f1" stopOpacity="0.8" />
              <stop offset="100%" stopColor="#4f46e5" stopOpacity="1" />
            </linearGradient>
          </defs>

          {/* Subtle full border frame on hover */}
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="30"
            ry="30"
            fill="none"
            stroke="#e0e7ff"
            strokeWidth="1.5"
          />

          {/* Outer Blurred Aura Dot Orbiting */}
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="30"
            ry="30"
            pathLength="100"
            fill="none"
            stroke="#6366f1"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray="5 95"
            filter={`url(#orbit-glow-${index}-${isMobile ? 'm' : 'd'})`}
            className="animate-border-orbit"
          />

          {/* Core Bright Glowing Dot Orbiting */}
          <rect
            x="1"
            y="1"
            width="calc(100% - 2px)"
            height="calc(100% - 2px)"
            rx="30"
            ry="30"
            pathLength="100"
            fill="none"
            stroke={`url(#dotGradient-${index}-${isMobile ? 'm' : 'd'})`}
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="3.5 96.5"
            className="animate-border-orbit"
          />
        </svg>
      </div>

      {/* Background Number */}
      <div className="absolute top-4 right-6 text-7xl font-display font-black text-slate-100 leading-none select-none pointer-events-none group-hover:text-indigo-50/80 transition-colors">
        {serviceNumber}
      </div>
      
      <div className="relative z-10 h-full flex flex-col">
        <div className="flex justify-between items-start mb-6">
          <div className="w-14 h-14 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 border border-indigo-100 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
            {IconComponent && <IconComponent size={26} />}
          </div>
          {isMobile && (
            <span className="px-3 py-1 bg-indigo-50/80 text-indigo-700 text-xs font-bold rounded-full border border-indigo-100">
              {index + 1} / {totalServices}
            </span>
          )}
        </div>
        
        <h3 className="text-xl font-display font-bold text-slate-900 mb-3 tracking-tight">
          {service.title}
        </h3>
        
        <p className="text-slate-600 text-sm leading-relaxed mb-6 flex-grow font-normal">
          {service.description}
        </p>
        
        <div className="flex flex-wrap gap-2 mb-6">
          {service.tags?.map((tag: string, i: number) => (
            <span key={i} className="px-2.5 py-1 bg-slate-100 rounded-md text-[10px] font-bold text-slate-700 border border-slate-200">
              {tag}
            </span>
          ))}
        </div>
        
        <div className="pt-5 border-t border-slate-100 flex items-center justify-between mt-auto">
          <button 
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            aria-label={`Initialize project for ${service.title}`}
            className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-900 group-hover:text-indigo-600 transition-colors cursor-pointer"
          >
            <span>{inquireLabel}</span>
            <LucideIcons.ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};

const Services: React.FC = () => {
  const [currentMobileIndex, setCurrentMobileIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);
  const { t, isUrdu } = useLanguage();

  const servicesList = t.services.items;

  const nextSlide = useCallback(() => {
    setCurrentMobileIndex((prev) => (prev + 1) % servicesList.length);
  }, [servicesList.length]);

  const prevSlide = useCallback(() => {
    setCurrentMobileIndex((prev) => (prev - 1 + servicesList.length) % servicesList.length);
  }, [servicesList.length]);

  // Automatic left-to-right rotation for mobile view every 3.5 seconds
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      nextSlide();
    }, 3500);

    return () => clearInterval(interval);
  }, [isPaused, nextSlide]);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.targetTouches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.targetTouches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (touchStartX.current !== null && touchEndX.current !== null) {
      const diff = touchStartX.current - touchEndX.current;
      if (diff > 40) {
        // Swiped left -> Rotate next
        nextSlide();
      } else if (diff < -40) {
        // Swiped right -> Rotate prev
        prevSlide();
      }
    }
    touchStartX.current = null;
    touchEndX.current = null;

    // Resume rotation after 3 seconds of inactivity
    setTimeout(() => {
      setIsPaused(false);
    }, 3000);
  };

  return (
    <section 
      id="services" 
      itemScope 
      itemType="https://schema.org/OfferCatalog"
      aria-label="Professional Digital & Development Services"
      className="py-14 sm:py-20 bg-white relative overflow-hidden w-full"
    >
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full bg-slate-50/50 z-0"></div>
      
      <div className="w-full px-4 sm:px-10 lg:px-16 xl:px-20 relative z-10 reveal">
        <div className="flex flex-col items-center mb-10 sm:mb-16 text-center max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2.5 px-4 py-2 bg-white border border-slate-200 rounded-full mb-6 shadow-sm">
             <LucideIcons.Cpu size={16} className="text-indigo-600" />
             <span className="text-slate-700 text-[10px] font-bold tracking-widest uppercase">
               {t.services.badge}
             </span>
          </div>
          <h2 className="text-3xl sm:text-5xl font-display font-extrabold tracking-tight text-slate-900 mb-4 sm:mb-6">
            {t.services.headingPrefix} <span className="text-indigo-600">{t.services.headingHighlight}</span>
          </h2>
          <p className="text-slate-600 text-sm sm:text-lg max-w-2xl font-normal leading-relaxed">
            {t.services.subheading}
          </p>
        </div>

        {/* 1. DESKTOP VIEW: Full Grid layout (Visible on md and above) */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-8 w-full">
          {servicesList.map((service, index) => (
            <ServiceCard 
              key={index} 
              service={service} 
              index={index} 
              totalServices={servicesList.length}
              inquireLabel={t.services.inquireNow}
            />
          ))}
        </div>

        {/* 2. MOBILE VIEW: Left-to-Right Rotating Carousel Slider (Visible ONLY on mobile < md) */}
        <div className="block md:hidden w-full max-w-full">
          {/* Carousel Track Container */}
          <div 
            className="relative w-full overflow-hidden rounded-[2rem]"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div 
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentMobileIndex * 100}%)` }}
            >
              {servicesList.map((service, index) => (
                <div key={index} className="w-full flex-shrink-0 px-1">
                  <ServiceCard 
                    service={service} 
                    index={index} 
                    isMobile={true} 
                    totalServices={servicesList.length}
                    inquireLabel={t.services.inquireNow}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Mobile Rotation Controls & Indicators */}
          <div className="mt-6 flex flex-col items-center gap-4">
            {/* Dots Navigation */}
            <div className="flex items-center gap-2">
              {servicesList.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setCurrentMobileIndex(idx);
                    setIsPaused(true);
                    setTimeout(() => setIsPaused(false), 3000);
                  }}
                  aria-label={`Go to service slide ${idx + 1}`}
                  className={`h-2 transition-all duration-300 rounded-full cursor-pointer ${
                    currentMobileIndex === idx 
                      ? 'w-7 bg-indigo-600' 
                      : 'w-2 bg-slate-300 hover:bg-slate-400'
                  }`}
                />
              ))}
            </div>

            {/* Left & Right Rotate Navigation Buttons */}
            <div className="flex items-center justify-between w-full px-2">
              <button
                onClick={() => {
                  prevSlide();
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 3000);
                }}
                aria-label="Previous service"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-xs font-bold text-slate-700 shadow-sm active:scale-95 transition-all hover:border-indigo-300 hover:text-indigo-600 cursor-pointer"
              >
                <LucideIcons.ChevronLeft size={16} />
                <span>Prev</span>
              </button>

              <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                <span>{isPaused ? 'Paused' : 'Auto Rotating'}</span>
              </div>

              <button
                onClick={() => {
                  nextSlide();
                  setIsPaused(true);
                  setTimeout(() => setIsPaused(false), 3000);
                }}
                aria-label="Next service"
                className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-full text-xs font-bold text-slate-700 shadow-sm active:scale-95 transition-all hover:border-indigo-300 hover:text-indigo-600 cursor-pointer"
              >
                <span>Next</span>
                <LucideIcons.ChevronRight size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;